/**
 * Page self-capture — a widget that lives on a page reads that page.
 *
 * Captures visible text from the host page (optionally narrowed by a CSS
 * selector), normalized and capped, plus a SHA-256 hash used for the
 * server-side cache lookup so unchanged pages never re-upload.
 */

/** Must match MAX_PAGE_TEXT_LENGTH on the server. */
export const MAX_PAGE_TEXT_LENGTH = 200_000;

export interface PageCapture {
  url: string;
  text: string;
}

/**
 * The page's text as the tutor should read it.
 *
 * innerText gives rendered text, which keeps hidden navigation and skip-links out — but
 * it also drops the body of every closed <details>. Course pages put real content there:
 * every TME 310 assignment has an "If you do not know where to start" hint in one, and
 * the tutor was reading the heading with nothing under it and telling students the
 * section was empty. So closed <details> are opened for the read and closed again.
 *
 * Authors can keep a block from the tutor with data-tutor="skip" (e.g. an answer reveal
 * they want students to work out first); it is hidden for the read.
 *
 * Everything happens in one synchronous stretch: the browser does not paint in between,
 * so the reader never sees a block flicker open.
 */
function readForTutor(root: HTMLElement): string {
  const opened = Array.from(root.querySelectorAll<HTMLDetailsElement>("details:not([open])"));
  // The style attribute itself is saved and put back, so the page is left byte-for-byte
  // as it was (re-serialising through node.style would normalise the author's text).
  const skipped = Array.from(root.querySelectorAll<HTMLElement>('[data-tutor="skip"]')).map((node) => ({
    node,
    style: node.getAttribute("style"),
  }));
  try {
    for (const d of opened) d.open = true;
    for (const s of skipped) s.node.style.setProperty("display", "none", "important");
    return root.innerText || "";
  } finally {
    for (const d of opened) d.open = false;
    for (const s of skipped) {
      if (s.style === null) s.node.removeAttribute("style");
      else s.node.setAttribute("style", s.style);
    }
  }
}

export function capturePageText(contentSelector?: string): PageCapture | null {
  let el: HTMLElement | null = null;
  if (contentSelector) {
    el = document.querySelector<HTMLElement>(contentSelector);
    if (!el) {
      console.warn(`[UWT Tutor Widget] data-content-selector "${contentSelector}" matched nothing; falling back to <body>.`);
    }
  }
  el = el ?? document.body;

  let text = readForTutor(el);
  // Collapse whitespace runs so hashes are stable against layout-only changes.
  text = text.replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();

  if (!text) return null;
  if (text.length > MAX_PAGE_TEXT_LENGTH) {
    text = text.slice(0, MAX_PAGE_TEXT_LENGTH);
  }

  // Fragment stripped to match server-side URL normalization.
  const url = window.location.origin + window.location.pathname + window.location.search;
  return { url, text };
}

/**
 * The page the widget is on, for the professor's Student Chats table. Sent with every
 * new conversation, by every widget — not just self-reading ones, and never the page's
 * content. Same address normalization as capturePageText (no fragment).
 */
export function hostPage(): { pageUrl: string; pageTitle?: string } {
  const pageUrl = window.location.origin + window.location.pathname + window.location.search;
  const pageTitle = document.title.trim() || undefined;
  return { pageUrl, pageTitle };
}

export async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
